// Apple Spotlight-style universal search overlay.
//
// Source-of-truth: portal-standalone.html L7837–7990 (#portal-search-overlay).
// Bound to the top-bar search button + Cmd/Ctrl-K. Type to filter across:
//   • Pages      — every primary route in the platform
//   • Actions    — direct commands (new customer, new quote, …)
//   • Settings   — deep links into Settings tabs
//   • Customers  — live search of /api/portal/contacts (debounced)
//   • Links      — curated external trade portals
//
// Keyboard: ↑/↓ moves the selection, Enter activates, Esc closes,
// backdrop click closes. Focus is trapped on the input.

import { renderIcon, lockScroll, unlockScroll, type IconName } from "@tkos/shared-ui";
import { api } from "@tkos/api-client";

import { openNewCustomerModal } from "./new-customer-modal";
import { navigate } from "./router";
import type { ToastKind } from "./types";

import "./spotlight.css";

interface SpotlightDeps {
  showToast: (msg: string, kind?: ToastKind) => void;
}

interface SpotlightItem {
  id: string;
  section: string;
  title: string;
  sub: string;
  icon: IconName;
  bg: string;
  iconColor: string;
  run: () => void;
}

interface CustomerHit {
  id?: string;
  name?: string;
  subtitle?: string;
}

const OVERLAY_ID = "tkos-spotlight";

let activeDeps: SpotlightDeps | null = null;
let overlay: HTMLElement | null = null;
let inputEl: HTMLInputElement | null = null;
let resultsEl: HTMLElement | null = null;
let escListener: ((e: KeyboardEvent) => void) | null = null;
let customerTimer: ReturnType<typeof setTimeout> | null = null;
let customerSeq = 0;
let currentItems: SpotlightItem[] = [];
let selIdx = -1;

function pagesCatalog(): SpotlightItem[] {
  return [
    { section: "Pages", id: "p:dashboard", title: "Dashboard", sub: "Overview, stats, widgets", icon: "dashboard", bg: "rgba(229,130,0,.12)", iconColor: "#e58200", run: () => navigate("dashboard") },
    { section: "Pages", id: "p:submissions", title: "Submissions", sub: "Wizard leads & quote enquiries", icon: "file", bg: "rgba(59,130,246,.12)", iconColor: "#3b82f6", run: () => navigate("submissions") },
    { section: "Pages", id: "p:messages", title: "Messages", sub: "Customer & supplier inbox", icon: "chat", bg: "rgba(34,197,94,.12)", iconColor: "#22c55e", run: () => navigate("messages") },
    { section: "Pages", id: "p:calendar", title: "Calendar", sub: "Events, installs & surveys", icon: "calendar", bg: "rgba(168,85,247,.12)", iconColor: "#a855f7", run: () => navigate("calendar") },
    { section: "Pages", id: "p:bookings", title: "Bookings", sub: "Appointments & consultations", icon: "calendar", bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: () => navigate("bookings") },
    { section: "Pages", id: "p:deliveries", title: "Deliveries", sub: "Track deliveries & dispatch", icon: "truck", bg: "rgba(34,197,94,.12)", iconColor: "#22c55e", run: () => navigate("deliveries") },
    { section: "Pages", id: "p:suppliers", title: "Suppliers", sub: "Supplier directory & pricing", icon: "package", bg: "rgba(14,165,233,.12)", iconColor: "#0ea5e9", run: () => navigate("suppliers") },
    { section: "Pages", id: "p:crm", title: "CRM & Comms", sub: "Customers, contacts & comms", icon: "team", bg: "rgba(236,72,153,.12)", iconColor: "#ec4899", run: () => navigate("crm") },
    { section: "Pages", id: "p:contacts", title: "Contacts", sub: "Customer, supplier & trade phonebook", icon: "team", bg: "rgba(6,182,212,.12)", iconColor: "#06b6d4", run: () => navigate("contacts") },
    { section: "Pages", id: "p:operations", title: "Operations", sub: "Tasks, jobs & workflows", icon: "gear", bg: "rgba(139,92,246,.12)", iconColor: "#8b5cf6", run: () => navigate("operations") },
    { section: "Pages", id: "p:reports", title: "Reports & Analytics", sub: "Revenue, conversions, reports", icon: "chart", bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: () => navigate("reports") },
    { section: "Pages", id: "p:quote", title: "Quote Builder", sub: "Split-screen quote with supplier catalogue", icon: "pound", bg: "rgba(34,197,94,.12)", iconColor: "#22c55e", run: () => navigate("quote-builder") },
    { section: "Pages", id: "p:product-search", title: "Product Search", sub: "Find products across suppliers", icon: "search", bg: "rgba(99,102,241,.12)", iconColor: "#6366f1", run: () => navigate("product-search") },
    { section: "Pages", id: "p:catalogue", title: "Catalogue Manager", sub: "Manage supplier catalogue feeds", icon: "folder", bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: () => navigate("catalogue-manager") },
    { section: "Pages", id: "p:designs", title: "Designs", sub: "Uploaded CAD files & renders", icon: "file", bg: "rgba(99,102,241,.12)", iconColor: "#6366f1", run: () => navigate("designs") },
    { section: "Pages", id: "p:drivers", title: "Drivers", sub: "Manage delivery drivers", icon: "truck", bg: "rgba(16,185,129,.12)", iconColor: "#10b981", run: () => navigate("drivers") },
    // Social Media AI removed from nav for V1 (returns in V1.5); route kept dormant.
    { section: "Pages", id: "p:settings", title: "Settings", sub: "Team, integrations & branding", icon: "gear", bg: "rgba(113,113,122,.12)", iconColor: "#71717a", run: () => navigate("settings") },
  ];
}

function actionsCatalog(deps: SpotlightDeps): SpotlightItem[] {
  return [
    {
      section: "Actions",
      id: "a:new-customer",
      title: "New customer…",
      sub: "Create a new customer record",
      icon: "customer",
      bg: "rgba(6,182,212,.12)",
      iconColor: "#06b6d4",
      run: () => {
        navigate("crm");
        openNewCustomerModal({ showToast: deps.showToast });
      },
    },
    {
      section: "Actions",
      id: "a:new-quote",
      title: "New quote…",
      sub: "Open the Quote Builder",
      icon: "file",
      bg: "rgba(34,197,94,.12)",
      iconColor: "#22c55e",
      run: () => navigate("quote-builder", { fresh: "1" }),
    },
    // AI Renders + Social Media AI removed from nav for V1 (return in V1.5
    // when properly built); modal + route code kept dormant for easy restore.
  ];
}

function settingsCatalog(): SpotlightItem[] {
  const go = (tab: string): (() => void) => () => navigate("settings", { tab });
  return [
    { section: "Settings", id: "s:team", title: "Team & Permissions", sub: "Add staff, manage roles and access", icon: "team", bg: "rgba(99,102,241,.12)", iconColor: "#6366f1", run: go("team-permissions") },
    { section: "Settings", id: "s:integrations", title: "Integrations", sub: "Xero, QuickBooks, Sage and more", icon: "link", bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: go("integrations") },
    { section: "Settings", id: "s:branding", title: "Branding", sub: "Brand colour & logo — applies everywhere", icon: "palette", bg: "rgba(217,70,239,.12)", iconColor: "#d946ef", run: go("branding") },
    { section: "Settings", id: "s:wizard-content", title: "Wizard Content", sub: "Wizard welcome copy, font & button style", icon: "sparkle", bg: "rgba(217,70,239,.12)", iconColor: "#d946ef", run: go("wizard-branding") },
    { section: "Settings", id: "s:telephony", title: "Telephony", sub: "Twilio numbers & call masking", icon: "phone", bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: go("telephony") },
    { section: "Settings", id: "s:notifications", title: "Notification preferences", sub: "Per-channel alert settings", icon: "bell", bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: go("notifications") },
    { section: "Settings", id: "s:payments", title: "Payments", sub: "Stripe Connect setup & payouts", icon: "card", bg: "rgba(34,197,94,.12)", iconColor: "#22c55e", run: go("payments") },
  ];
}

function linksCatalog(): SpotlightItem[] {
  return [
    { section: "Links", id: "l:blum", title: "Blum e-services", sub: "blum.com", icon: "link", bg: "rgba(59,130,246,.12)", iconColor: "#3b82f6", run: () => window.open("https://blum.com", "_blank") },
    { section: "Links", id: "l:hafele", title: "Häfele Trade Portal", sub: "hafele.co.uk", icon: "link", bg: "rgba(34,197,94,.12)", iconColor: "#22c55e", run: () => window.open("https://hafele.co.uk", "_blank") },
    { section: "Links", id: "l:pws", title: "PWS Trade Login", sub: "pws.co.uk", icon: "link", bg: "rgba(168,85,247,.12)", iconColor: "#a855f7", run: () => window.open("https://pws.co.uk", "_blank") },
    { section: "Links", id: "l:hettich", title: "Hettich Product Finder", sub: "hettich.com", icon: "link", bg: "rgba(239,68,68,.12)", iconColor: "#ef4444", run: () => window.open("https://hettich.com", "_blank") },
    { section: "Links", id: "l:uform", title: "Uform Trade", sub: "uform.co.uk", icon: "link", bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: () => window.open("https://uform.co.uk", "_blank") },
    { section: "Links", id: "l:tkc", title: "TKC Trade", sub: "tkcomponents.co.uk", icon: "link", bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: () => window.open("https://tkcomponents.co.uk", "_blank") },
  ];
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function tokenizeMatch(text: string, q: string): boolean {
  if (!q) return true;
  const lc = text.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => lc.includes(t));
}

function filterStatic(deps: SpotlightDeps, q: string): SpotlightItem[] {
  const all: SpotlightItem[] = [
    ...pagesCatalog(),
    ...actionsCatalog(deps),
    ...settingsCatalog(),
    ...linksCatalog(),
  ];
  return all.filter((it) => tokenizeMatch(`${it.title} ${it.sub}`, q));
}

function customerHitToItem(hit: CustomerHit): SpotlightItem {
  return {
    section: "Customers",
    id: `c:${hit.id ?? hit.name ?? Math.random().toString(36).slice(2)}`,
    title: hit.name ?? "Unnamed",
    sub: hit.subtitle ?? "Customer record",
    icon: "customer",
    bg: "rgba(245,158,11,.12)",
    iconColor: "#f59e0b",
    run: () => {
      if (hit.id) navigate("customer", { id: hit.id });
      else navigate("crm");
    },
  };
}

async function fetchCustomers(q: string, deps: SpotlightDeps): Promise<SpotlightItem[]> {
  if (!q) return [];
  try {
    const res = await api<{ ok?: boolean; contacts?: CustomerHit[]; error?: string }>(
      `/portal/contacts?type=customer&q=${encodeURIComponent(q)}`,
    );
    return (res.contacts ?? []).slice(0, 8).map(customerHitToItem);
  } catch (err) {
    console.error("[spotlight] customer search failed", err);
    void deps;
    return [];
  }
}

function paint(items: SpotlightItem[]): void {
  if (!resultsEl) return;
  currentItems = items;
  selIdx = items.length > 0 ? 0 : -1;

  if (items.length === 0) {
    resultsEl.innerHTML = `<div class="tkos-spot__empty">No matches.</div>`;
    return;
  }

  const sections: string[] = [];
  let last = "";
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) continue;
    if (it.section !== last) {
      sections.push(`<div class="tkos-spot__section">${escapeHtml(it.section)}</div>`);
      last = it.section;
    }
    sections.push(
      `<button type="button" class="tkos-spot__item${i === selIdx ? " is-active" : ""}" data-i="${i}">
        <span class="tkos-spot__icon" style="background:${escapeHtml(it.bg)};color:${escapeHtml(it.iconColor)}">
          ${renderIcon(it.icon, { size: "sm" }).outerHTML}
        </span>
        <span class="tkos-spot__lbl">
          <span class="tkos-spot__title">${escapeHtml(it.title)}</span>
          <span class="tkos-spot__sub">${escapeHtml(it.sub)}</span>
        </span>
      </button>`,
    );
  }
  resultsEl.innerHTML = sections.join("");
  for (const btn of resultsEl.querySelectorAll<HTMLButtonElement>("button[data-i]")) {
    btn.addEventListener("click", () => {
      const idx = Number(btn.dataset["i"]);
      runItem(idx);
    });
    btn.addEventListener("mouseenter", () => {
      const idx = Number(btn.dataset["i"]);
      moveSelection(idx);
    });
  }
}

function moveSelection(idx: number): void {
  if (!resultsEl) return;
  if (idx < 0 || idx >= currentItems.length) return;
  selIdx = idx;
  for (const btn of resultsEl.querySelectorAll<HTMLButtonElement>("button[data-i]")) {
    btn.classList.toggle("is-active", Number(btn.dataset["i"]) === idx);
  }
  const sel = resultsEl.querySelector<HTMLButtonElement>(`button[data-i="${idx}"]`);
  sel?.scrollIntoView({ block: "nearest" });
}

function runItem(idx: number): void {
  const it = currentItems[idx];
  if (!it) return;
  close();
  try {
    it.run();
  } catch (err) {
    console.error("[spotlight] run failed", err);
  }
}

function handleInput(q: string, deps: SpotlightDeps): void {
  const staticHits = filterStatic(deps, q);
  paint(staticHits);

  if (customerTimer) clearTimeout(customerTimer);
  if (!q) return;
  const mySeq = ++customerSeq;
  customerTimer = setTimeout(() => {
    void fetchCustomers(q, deps).then((items) => {
      if (mySeq !== customerSeq) return;
      // Re-filter static against latest q in case the user kept typing
      // (we don't have q stored — read straight off the input).
      const liveQ = inputEl?.value.trim() ?? q;
      const stat = filterStatic(deps, liveQ);
      paint([...stat, ...items]);
    });
  }, 220);
}

function buildOverlay(deps: SpotlightDeps): void {
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "tkos-spot";

  const box = document.createElement("div");
  box.className = "tkos-spot__box";

  const row = document.createElement("div");
  row.className = "tkos-spot__input-row";
  row.appendChild(renderIcon("search", { size: "sm" }));

  inputEl = document.createElement("input");
  inputEl.type = "search";
  inputEl.className = "tkos-spot__input";
  inputEl.placeholder = "Search the OS — pages, customers, settings, actions…";
  inputEl.autocomplete = "off";
  inputEl.spellcheck = false;

  const esc = document.createElement("span");
  esc.className = "tkos-spot__esc";
  esc.textContent = "Esc";
  esc.addEventListener("click", () => close());

  row.append(inputEl, esc);

  resultsEl = document.createElement("div");
  resultsEl.className = "tkos-spot__results";

  box.append(row, resultsEl);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  inputEl.addEventListener("input", () => {
    handleInput(inputEl?.value.trim() ?? "", deps);
  });

  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveSelection(Math.min(selIdx + 1, currentItems.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveSelection(Math.max(selIdx - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (selIdx >= 0) runItem(selIdx);
    }
  });
}

function open(deps: SpotlightDeps): void {
  activeDeps = deps;
  buildOverlay(deps);
  if (!overlay || !inputEl) return;
  overlay.classList.add("is-open");
  lockScroll();
  inputEl.value = "";
  handleInput("", deps);
  setTimeout(() => inputEl?.focus(), 30);

  if (!escListener) {
    escListener = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && overlay?.classList.contains("is-open")) {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", escListener);
  }
}

function close(): void {
  if (!overlay) return;
  overlay.classList.remove("is-open");
  unlockScroll();
  if (escListener) {
    document.removeEventListener("keydown", escListener);
    escListener = null;
  }
}

export function openSpotlight(deps: SpotlightDeps): void {
  open(deps);
}

/**
 * Install the global Cmd/Ctrl-K shortcut. Should be called once when the
 * shell mounts; uses the same SpotlightDeps the search button uses.
 */
export function installSpotlightShortcut(deps: SpotlightDeps): () => void {
  activeDeps = deps;
  const onKey = (e: KeyboardEvent): void => {
    const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
    if (!isCmdK) return;
    e.preventDefault();
    open(activeDeps ?? deps);
  };
  document.addEventListener("keydown", onKey);
  return () => document.removeEventListener("keydown", onKey);
}
